import { FilterInfo } from "@lhd/features/Apps/AppList/ManagementAppSheet/ManagementAppSheetList/AppSheetComponents/FilterIcon";

export interface FilterPayload {
  active_filters: Record<string, string[]>;
  filter_order: string[];
}

const hasUncheckAllValue = (value: any): boolean => {
  if (value === "__NOT_FOUND__") return true;

  if (Array.isArray(value)) {
    return value.includes("__NOT_FOUND__") || value.length === 0;
  }

  if (value && typeof value === "object") {
    const values = Object.values(value);
    // Treat empty object as having unchecked all
    if (values.length === 0) return true;
    return values.some((nestedValue) => hasUncheckAllValue(nestedValue));
  }

  return false;
};

export const checkHasEmptyFilter = (filterInfo: FilterInfo): boolean => {
  const { filter = {}, filterOrder = [] } = filterInfo;
  return filterOrder.some((columnId) => {
    const config = filter[columnId];
    if (!config) return false;
    const rawFilter = config.filter;
    if (rawFilter === undefined || rawFilter === null) return false;

    const isFilterEmpty = hasUncheckAllValue(rawFilter);
    const hasSpecialEmpty = !!config.filter_special_empty;
    return isFilterEmpty && !hasSpecialEmpty;
  });
};

export const checkHasEmptyFilterBefore = (
  filterInfo: FilterInfo,
  currentColumnId: string | null,
): boolean => {
  const { filter = {}, filterOrder = [] } = filterInfo;
  const currentIndex = filterOrder.indexOf(String(currentColumnId));
  const endIndex = currentIndex === -1 ? filterOrder.length : currentIndex;
  for (let i = 0; i < endIndex; i++) {
    const col = filterOrder[i];
    const cfg = filter[col];
    if (!cfg) continue;
    const raw = cfg.filter;
    if (raw === undefined || raw === null) continue;
    const isEmpty = hasUncheckAllValue(raw);
    const hasSpecial = !!cfg.filter_special_empty;
    if (isEmpty && !hasSpecial) return true;
  }
  return false;
};

export const normalizeFilterPayload = (
  fieldKey: string,
  payload: FilterPayload,
): FilterPayload => {
  const { active_filters, filter_order } = payload;
  const idx = filter_order.indexOf(fieldKey);
  if (idx === -1) return payload;
  const upstream = filter_order.slice(0, idx);
  const normalized: Record<string, string[]> = {};
  upstream.forEach((k) => {
    if (active_filters[k]) normalized[k] = active_filters[k];
  });
  return { active_filters: normalized, filter_order: upstream };
};

export const extractFilterValuesForColumn = (
  columnId: string,
  config: any,
  formatValues?: (columnId: string, values: string[]) => string[],
): string[] => {
  const raw = config?.filter;
  if (hasUncheckAllValue(raw)) {
    return config?.filter_special_empty ? ["__NULL__"] : [];
  }

  const format = formatValues || ((_, values) => values);

  let values: string[] = [];
  if (raw) {
    const original: string[] = Array.isArray(raw)
      ? raw.map(String)
      : Object.keys(raw)
          .filter((key) => raw[key] === true)
          .map(String);
    if (original.length > 0) {
      values = format(columnId, original);
    }
  }
  if (config?.filter_special_empty) {
    values = [...values, "__NULL__"];
  }
  const search = config?.search;
  if (search != null && search !== "") {
    const opts = config?.filterOptions || [];
    const matched = opts.filter((o: any) => {
      const lbl = o.label ? String(o.label).toLowerCase() : "";
      return lbl.includes(search.trim().toLowerCase());
    });
    const searchVals = matched.map((o: any) => String(o.value));
    const formatted = format(columnId, searchVals);
    if (values.length > 0) {
      const combined = Array.from(new Set([...values, ...formatted]));
      values = combined.length > 0 ? combined : ["__NOT_FOUND__"];
    } else {
      values = formatted.length > 0 ? formatted : ["__NOT_FOUND__"];
    }
  }
  return values;
};

export const buildActiveFilters = (
  filter: Record<string, any> = {},
  getFieldKey: (columnId: string) => string,
  extractValues: (columnId: string, config: any) => string[],
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  Object.entries(filter).forEach(([colId, cfg]) => {
    const vals = extractValues(colId, cfg);
    if (vals.length > 0) {
      result[getFieldKey(colId)] = vals;
    }
  });
  return result;
};

export const convertFilterOrderToFieldKeys = (
  filterOrder: string[],
  getFieldKey: (columnId: string) => string,
): string[] => {
  return filterOrder.map((col) => getFieldKey(col));
};

export const handleGetParamsFilterWithFieldKey = (
  filterInfo: FilterInfo,
  getFieldKey: (columnId: string) => string,
  extractValues: (columnId: string, config: any) => string[],
): Record<string, any> => {
  const { filter = {} } = filterInfo;
  const params: Record<string, any> = {};
  const sortParams: string[] = [];

  Object.entries(filter).forEach(([columnId, config]: [string, any]) => {
    const fieldKey = getFieldKey(columnId);
    const filterKey = `filters[${fieldKey}][]`;

    const sort = config?.sort;
    if (sort === "asc" || sort === "desc") {
      sortParams.push(`${fieldKey}:${sort}`);
    }

    const values = extractValues(columnId, config);
    if (values.length > 0) {
      params[filterKey] = values;
    }
  });

  if (sortParams.length > 0) {
    params["sort[]"] = sortParams;
  }

  return params;
};

export const formatJapaneseDateValue = (value: string): string => {
  let result = String(value);

  // YYYY年MM月DD日 HH:mm:ss
  result = result.replace(
    /^(\d{4})年(\d{1,2})月(\d{1,2})日\s*(\d{1,2}):(\d{1,2}):(\d{1,2})$/,
    (_, y, m, d, h, min, s) =>
      `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")} ${h.padStart(
        2,
        "0",
      )}:${min.padStart(2, "0")}:${s.padStart(2, "0")}`,
  );

  // YYYY年MM月DD日 HH:mm
  result = result.replace(
    /^(\d{4})年(\d{1,2})月(\d{1,2})日\s*(\d{1,2}):(\d{1,2})$/,
    (_, y, m, d, h, min) =>
      `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")} ${h.padStart(
        2,
        "0",
      )}:${min.padStart(2, "0")}`,
  );

  // YYYY年MM月DD日 HH
  result = result.replace(
    /^(\d{4})年(\d{1,2})月(\d{1,2})日\s*(\d{1,2})$/,
    (_, y, m, d, h) =>
      `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")} ${h.padStart(2, "0")}`,
  );

  result = result.replace(
    /^(\d{4})年(\d{1,2})月(\d{1,2})日$/,
    (_, y, m, d) => `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`,
  );
  result = result.replace(
    /^(\d{4})年(\d{1,2})月$/,
    (_, y, m) => `${y}-${m.padStart(2, "0")}`,
  );
  result = result.replace(/^(\d{4})年$/, "$1");
  return result;
};

export const formatFilterValues = (
  columnId: string,
  values: string[],
  isDateField: (columnId: string) => boolean,
): string[] => {
  if (isDateField(columnId)) {
    return values.map(formatJapaneseDateValue);
  }
  return values;
};

export const denormalizeFilterParams = (
  params: Record<string, any>,
  sortFormat: "object" | "array" = "array",
): Record<string, any> => {
  const result: Record<string, any> = {};
  const sortObject: Record<string, string> = {};
  let sortArray: string[] = [];

  Object.entries(params).forEach(([key, value]) => {
    // Handle worker_filters[key][] → worker_filters.key
    if (key.startsWith("worker_filters[")) {
      if (!result.worker_filters) result.worker_filters = {};
      const filterKey = key
        .replace(/^worker_filters\[/, "")
        .replace(/\]\[\]?$/, "");
      result.worker_filters[filterKey] = value;
    }
    // Handle app_filters[key][] → app_filters.key
    else if (key.startsWith("app_filters[")) {
      if (!result.app_filters) result.app_filters = {};
      const filterKey = key
        .replace(/^app_filters\[/, "")
        .replace(/\]\[\]?$/, "");
      result.app_filters[filterKey] = value;
    }
    // Handle filters[key][] or filters[key] → filters.key
    else if (key.startsWith("filters[")) {
      if (!result.filters) result.filters = {};
      const filterKey = key.replace(/^filters\[/, "").replace(/\]\[\]?$/, "");
      result.filters[filterKey] = value;
    }
    // Handle sort[columnId] → collect into sort object
    else if (key.startsWith("sort[") && key !== "sort[]") {
      const columnId = key.replace(/^sort\[/, "").replace(/\]$/, "");
      sortObject[columnId] = value;
    }
    // Handle sort[]
    else if (key === "sort[]") {
      sortArray = Array.isArray(value) ? value : [value];
    }
    // Keep other params as is (search, etc.)
    else {
      result[key] = value;
    }
  });

  if (sortFormat === "object") {
    // If we have sort[] array, parse it into object format
    if (sortArray.length > 0) {
      sortArray.forEach((item) => {
        const lastColonIndex = item.lastIndexOf(":");
        if (lastColonIndex > 0) {
          const columnId = item.substring(0, lastColonIndex);
          const order = item.substring(lastColonIndex + 1);
          if (order === "asc" || order === "desc") {
            sortObject[columnId] = order;
          }
        }
      });
    }
    if (Object.keys(sortObject).length > 0) {
      result.sort = sortObject;
    }
  } else {
    if (sortArray.length > 0) {
      result.sort = sortArray;
    } else if (Object.keys(sortObject).length > 0) {
      result.sort = Object.entries(sortObject).map(([k, v]) => `${k}:${v}`);
    }
  }

  return result;
};
